/**
 * Stock list configuration file, including breadCrumb, tabs content, filter and
 * sort, and new stock item adding template.
 * Author: Teemo
 * CreatedAt: Unknown
 */

import moment from 'moment';
import React from 'react';

import Tooltip, {
  NoWrapContainer,
} from 'src/components/GlobalTooltip/GlobalTooltip';
import Link from 'src/components/Link/Link';
import { EnumFilterType } from 'src/constrains/Query1/Filter';
import css from './StockList.less';

import { isUserValid, storeResourceNameGenerator } from 'src/tools/getUserInfo';
import {
  EPermissionSource,
  EnumCategory,
  EnumOperation,
  EnumSubCategory,
} from 'src/types/common/resources';

const breadcrumbData = ({ getI18n }) => [
  {
    url: '',
    name: getI18n('general.sider.home'),
  },
  {
    url: '',
    name: getI18n('general.products'),
  },
  {
    url: '',
    name: getI18n('stockIn.stock.stocklistconfig_breadcrumbData_StockIn'),
  },
];

const tabsConfig = ({ getI18n }) => [{ name: getI18n('general.all'), key: '' }];

const filterList = (supplierList, staffList, { getI18n }) => {
  return [
    {
      type: EnumFilterType.MultipleSelection,
      dropdownProps: {
        name: getI18n('stockIn.stock.stocklistconfig_columns_supplier_title'),
      },
      keyWord: 'supplierUuid',
      options: supplierList.map((item) => ({
        label: item.name,
        value: item.uuid,
      })),
    },
    {
      type: EnumFilterType.MultipleSelection,
      dropdownProps: {
        name: getI18n('stockIn.stock.stocklistconfig_columns_byStaff_title'),
      },
      keyWord: 'staff',
      options: staffList,
    },
  ];
};

const sortList = ({ getI18n }) => [
  {
    label: getI18n('stockIn.stock.stocklistconfig_sortlist_newest'),
    value: getI18n('stockIn.stock.stocklistconfig_sortlist_newest'),
    data: { field: 'modifiedDate', ascent: false },
  },
  {
    label: getI18n('stockIn.stock.stocklistconfig_sortlist_Oldest'),
    value: getI18n('stockIn.stock.stocklistconfig_sortlist_Oldest'),
    data: { field: 'modifiedDate', ascent: true },
  },
  {
    label: getI18n('stockIn.stock.stocklistconfig_sortlist_total_highest'),
    value: getI18n('stockIn.stock.stocklistconfig_sortlist_total_highest'),
    data: { field: 'total', ascent: false },
  },
  {
    label: getI18n('stockIn.stock.stocklistconfig_sortlist_total_lowest'),
    value: getI18n('stockIn.stock.stocklistconfig_sortlist_total_lowest'),
    data: { field: 'total', ascent: true },
  },
];

const columns = ({ getI18n }) => [
  {
    title: getI18n('stockIn.stock.stocklistconfig_columns_invoiceNo_title'),
    dataIndex: 'orderNo',
    key: 'orderNo',
    default: true,
    optional: false,
    className: css.columnClass,
    width: 176,
    childGenerate: (text, record) => {
      const canView = isUserValid({
        source: EPermissionSource.store,
        resourcesGroup: [
          storeResourceNameGenerator({
            category: EnumCategory.Products,
            subCategory: EnumSubCategory.ProductsStockIn,
            operation: EnumOperation.view_stock_details,
          }),
        ],
      });
      return canView ? (
        <NoWrapContainer className={css.orderNo}>
          <Link to={`/stockEditor/${record.uuid}`}>
            <Tooltip title={text}>{text}</Tooltip>
          </Link>
        </NoWrapContainer>
      ) : (
        <NoWrapContainer className={css.orderNo}>
          <Tooltip title={text}>{text}</Tooltip>
        </NoWrapContainer>
      );
    },
  },
  {
    title: getI18n('stockIn.stock.stocklistconfig_columns_invoiceDate_title'),
    dataIndex: 'modifiedDate',
    default: true,
    optional: false,
    key: 'modifiedDate',
    className: css.columnClass,
    childGenerate: (text) => (
      <NoWrapContainer>
        <Tooltip title={moment(text).format('MM-DD-YYYY')}>
          {moment(text).format('MM-DD-YYYY')}
        </Tooltip>
      </NoWrapContainer>
    ),
    minWidth: 176,
  },
  {
    title: getI18n('stockIn.stock.stocklistconfig_columns_supplier_title'),
    dataIndex: 'supplier',
    default: true,
    optional: false,
    key: 'supplier',
    className: css.columnClass,
    childGenerate: (text) => (
      <NoWrapContainer>
        <Tooltip title={text.name}>{text.name}</Tooltip>
      </NoWrapContainer>
    ),
    minWidth: 208,
  },
  {
    title: getI18n('stockIn.stock.stocklistconfig_columns_Warehouse_title'),
    dataIndex: ['warehouse', 'locationName'],
    default: true,
    optional: false,
    key: 'notes',
    className: css.columnClass,
    minWidth: 160,
  },
  {
    title: getI18n('stockIn.stock.stocklistconfig_columns_Notes_title'),
    dataIndex: 'notes',
    default: true,
    optional: false,
    key: 'notes',
    className: css.columnClass,
    minWidth: 224,
  },
  {
    title: getI18n('stockIn.stock.stocklistconfig_columns_byStaff_title'),
    dataIndex: 'staff',
    default: true,
    optional: false,
    key: 'staff',
    className: css.columnClass,
    minWidth: 144,
  },
  {
    title: (
      <div className={css.invoiceValue}>
        <p
          style={{
            wordBreak: 'keep-all',
          }}>
          {getI18n('stockIn.stock.stocklistconfig_columns_invoiceValue_title')}
        </p>
      </div>
    ),
    dataIndex: 'total',
    default: true,
    optional: false,
    key: 'total',
    align: 'right',
    className: css.columnClass,
    minWidth: 104,
    childGenerate: (text) => (
      <NoWrapContainer className={css.total}>
        <Tooltip title={text}>{Number(text || 0).toFixed(2)}</Tooltip>
      </NoWrapContainer>
    ),
  },
];

export { breadcrumbData, columns, filterList, sortList, tabsConfig };
